export type SessionUser = {
  id: string;
  email: string;
  display_name: string;
  account_id: string | null;
};

export type Project = {
  id: string;
  account_id: string;
  name: string;
  slug: string;
  system_prompt: string;
  status: string;
  created_at: string;
};

export type ApiKey = {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
};

export type CreatedApiKey = ApiKey & { secret: string };

type ApiError = { error?: string; message?: string };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
    ...init,
  });
  const data = (await response.json().catch(() => ({}))) as T & ApiError;
  if (!response.ok) throw new Error(data.error || data.message || `HTTP ${response.status}`);
  return data;
}

export async function getCurrentUser(): Promise<SessionUser | null> {
  try {
    const data = await request<{ user: SessionUser }>('/api/v1/auth/me');
    return data.user;
  } catch {
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<void> {
  await request('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
}

export async function registerAccount(displayName: string, email: string, password: string): Promise<void> {
  await request('/api/v1/auth/register', { method: 'POST', body: JSON.stringify({ display_name: displayName, email, password }) });
}

export async function signOut(): Promise<void> {
  await request('/api/v1/auth/logout', { method: 'POST' });
}

export async function listProjects(accountId: string): Promise<Project[]> {
  return (await request<{ projects: Project[] }>(`/api/v1/accounts/${accountId}/projects`)).projects;
}

export async function createProject(accountId: string, name: string, systemPrompt: string): Promise<Project> {
  return (await request<{ project: Project }>(`/api/v1/accounts/${accountId}/projects`, {
    method: 'POST', body: JSON.stringify({ name, system_prompt: systemPrompt }),
  })).project;
}

export async function listApiKeys(accountId: string, projectId: string): Promise<ApiKey[]> {
  return (await request<{ api_keys: ApiKey[] }>(`/api/v1/accounts/${accountId}/projects/${projectId}/api-keys`)).api_keys;
}

export async function createApiKey(accountId: string, projectId: string, name: string): Promise<CreatedApiKey> {
  return (await request<{ api_key: CreatedApiKey }>(`/api/v1/accounts/${accountId}/projects/${projectId}/api-keys`, {
    method: 'POST', body: JSON.stringify({ name }),
  })).api_key;
}

export async function revokeApiKey(accountId: string, projectId: string, keyId: string): Promise<void> {
  await request(`/api/v1/accounts/${accountId}/projects/${projectId}/api-keys/${keyId}`, { method: 'DELETE' });
}
